import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Typography,
  TextField,
  Switch,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
// Same icon set the AI Agent wizard uses for its own header back-arrow, step
// glyphs, and panel-header icon (Rahim, 2026-09-23: "use the same type of ui
// ux ... that page has icon and different design"). Reused directly, not
// re-picked, except where a concept genuinely differs (our 3 steps aren't
// AI Agent's 4, so they get their own fitting glyphs, same visual recipe).
import { ArrowLeft2, ArrowUp, Brush, Notification, Send2, Monitor, Message2, Microphone, Minus, Paperclip2 } from "iconsax-react";
import PanelHeading from "../../components/Navbar/PanelHeading";
import { getAccessToken } from "../../utils/tokenManager";
import { getAuthToken } from "../../utils/getAuthToken";
import { createChatbotWidget, fetchChatbotWidget, updateChatbotWidget } from "../../services/chatbotApi";
import CopyField from "../../components/Settings/Features/CopyField";
// Same "label above the field, not MUI's floating label" pattern + input
// styling + capped field width the AI Agent wizard uses. Reused directly.
import { LabeledField, inboxInputSx, SHORT_FIELD_WIDTH } from "../../components/Settings/Features/AiSettings/components/LabeledField";

// Mirrors the backend's ORIGIN_RE in createChatbotWidget.service.js — client-side
// feedback only, the server is the authoritative check.
const ORIGIN_PATTERN = /^https?:\/\/[a-zA-Z0-9.-]+(:\d+)?$/;
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const STEPS = ["Branding", "Engagement", "Deploy"];
const STEP_ICON_COMPONENTS = [Brush, Notification, Send2];
const POPUP_MAX_LEN = 500;
const CONSENT_MAX_LEN = 500;
const FIELD_SPACING = 2.5;
// Helper text under inputs (URL hint, character counters) rendered at MUI's
// tiny caption size — bumped to Small/Label (Rahim, 2026-09-23: "too damn small
// text"). Merged after inboxInputSx wherever a TextField has helperText.
const inputWithHelperSx = [inboxInputSx, { "& .MuiFormHelperText-root": { fontSize: "0.8125rem" } }];
// Read-only monospace fields on the Deploy step: our input styling, keys/snippet
// in monospace at the Small/Label size so long values fit.
const deployFieldSx = [inboxInputSx, { "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: "0.8125rem" } }];
// Wider than AI Agent's own SHORT_FIELD_WIDTH (Rahim, 2026-09-23: "a little
// more", then "increase the width of the input fields... for all the
// engagement and deploy") — 480 -> 560 -> 720. Local to this page only.
const PAGE_FIELD_WIDTH = SHORT_FIELD_WIDTH + 240;

// Inbox/card type scale, reused everywhere on this page instead of MUI's raw
// variant defaults — body1 renders 12px and body2 renders 10px in this theme,
// easy to miss since neither looks like an error at a glance (Rahim,
// 2026-09-23: "especially fix the font sizing").
const SECTION_TITLE_SX = { fontSize: "1rem", fontWeight: 600, color: "text.primary" }; // Section Title
const SECTION_BODY_SX = { fontSize: "0.8125rem", color: "text.secondary" }; // Small/Label
const BODY_SX = { fontSize: "0.875rem", color: "text.primary" }; // Body (default)
// The launcher/home/chat previews mimic the customer-facing widget, which is
// always light — so they use fixed colours, not theme ones (Rahim, 2026-09-23:
// "fix the colour of the texts for the dark mode, many text are black").
const WIDGET_CAPTION_SX = { fontSize: "0.75rem", color: "#6B7280" };
const widgetFieldSx = (mt) => ({
  mt,
  "& .MuiInputBase-input": { fontSize: "0.8125rem" },
  "& .MuiInputBase-input::placeholder": { color: "#9CA3AF", opacity: 1 },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#9CA3AF" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.15)" },
});

// Same violet accent + circle-icon treatment AgentConfigView.jsx's own
// WizardStepIcon uses for its 4-step stepper — copied here (that file
// doesn't export it) rather than imported, same "small shared sx duplicated
// into the leaf file" convention already used elsewhere in this codebase.
// Was AI Agent's #a78bfa; now the Web Chat card's own violet (Integrations.jsx
// CHANNELS -> chatbot color #7C5CFF) so the wizard matches the card you clicked
// Connect on (Rahim, 2026-09-23).
const STEP_FILLED_COLOR = "#7C5CFF";
function WizardStepIcon({ active, completed, index }) {
  const filled = active || completed;
  const StepGlyph = STEP_ICON_COMPONENTS[index] || Brush;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: filled ? STEP_FILLED_COLOR : "background.paper",
        border: filled ? "none" : "1.5px solid",
        borderColor: "divider",
        color: filled ? "#fff" : "text.secondary",
        transition: "background-color 0.15s, color 0.15s",
      }}
    >
      <Box sx={{ width: 19, height: 19, display: "flex" }}>
        <StepGlyph size="100%" color="currentColor" variant={filled ? "Bold" : "Linear"} />
      </Box>
    </Box>
  );
}

// Same neutral bordered button formula AgentConfigView.jsx's Previous/Next
// footer buttons use (its own local `neutralButtonSx`, not exported).
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

// Previous/Next only (Rahim, 2026-09-23: "i dont like its background colour
// white remove the background colour") — same border/text/hover as
// neutralButtonSx above, just no fill. Upload logo/Done keep the filled
// version, not called out.
const footerNavButtonSx = { ...neutralButtonSx, backgroundColor: "transparent" };

// Same header-action button formula AiSettings.jsx's Cancel/Save Agent
// buttons use (its own local `PANEL_HEADER_BUTTON_SX`, not exported).
const PANEL_HEADER_BUTTON_SX = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 500,
  px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
  py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
};

// Same bordered-card-with-icon-heading treatment AgentConfigView.jsx's own
// SectionCard uses to group each logical section of its Config step (that
// component isn't exported either — same local-copy convention). Wraps
// "Brand identity" (step 0) and "Pop-up message" (step 1) below; the
// top-level Widget name/Website URL fields stay outside any card, same as
// AI Agent's own Agent Name/Description sit outside its SectionCards.
function SectionCard({ heading, description, icon: Icon, children }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "transparent"),
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {heading && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          {Icon && (
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                mt: 0.125,
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.primary",
              }}
            >
              <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size="100%" color="currentColor" variant="Linear" />
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={SECTION_TITLE_SX}>{heading}</Typography>
            {description && <Typography sx={SECTION_BODY_SX}>{description}</Typography>}
          </Box>
        </Box>
      )}
      {children}
    </Box>
  );
}

/** Small labeled color input — a native color swatch + hex text field, no new dependency. */
const ColorField = ({ label, value, onChange, disabled }) => (
  <LabeledField label={label}>
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="input"
        type="color"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        // A native colour input draws a padded white/grey frame around the swatch
        // (very visible in dark mode) — flatten it so only the colour square shows
        // (Rahim, 2026-09-23: "remove the white coloured boxes").
        sx={{
          width: 40,
          height: 40,
          p: 0,
          // Light border so a black/very dark colour is still visible against the
          // dark page (Rahim, 2026-09-23). Same border colour as our text inputs.
          border: "1px solid",
          borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : theme.palette.divider),
          boxSizing: "border-box",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: "transparent",
          WebkitAppearance: "none",
          "&::-webkit-color-swatch-wrapper": { padding: 0 },
          "&::-webkit-color-swatch": { border: "none", borderRadius: "8px" },
          "&::-moz-color-swatch": { border: "none", borderRadius: "8px" },
        }}
      />
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        fullWidth
        sx={[inboxInputSx, { "& .MuiOutlinedInput-root": { minHeight: 40 } }]}
      />
    </Stack>
  </LabeledField>
);

// The colour inputs are free text, so mid-typing values like "#0" or a 3-digit
// "#06F" reach the preview. Appending an alpha suffix to those (`${color}33`)
// produces an invalid colour and the whole style silently drops — normalize to
// a full 6-digit hex first, falling back until the value is valid.
const normalizeHex = (value, fallback) => {
  const v = (value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return "#" + v.slice(1).split("").map((c) => c + c).join("");
  return fallback;
};

// The window that opens from the launcher — shared by the Homepage and Chat
// window previews so both are exactly the same size and position (Rahim,
// 2026-09-23: "use the same height and width of the panel in home page").
// Right edge lines up with the bubble's, top 64px under the panel top, ends
// 12px above the bubble (bubble top is 28 + 56 = 84px from the bottom).
// Always-light colours on purpose: it's the customer-facing widget, not app
// chrome, so it shouldn't flip with our dark mode.
const widgetWindowSx = (primary, secondary) => ({
  position: "absolute",
  top: 64,
  right: 30,
  bottom: 96,
  width: 460,
  maxWidth: "calc(100% - 50px)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
  backgroundImage: `linear-gradient(160deg, #FFFFFF 30%, ${primary}33 70%, ${secondary}33 100%)`,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  color: "#0D122C",
});

// Launcher bubble — the floating chat circle. Shared by the Entry point and
// Homepage previews so it's the same size/icon/position in both (Rahim,
// 2026-09-23: "add the icon under it as well, same position as the entry
// point"). Callers pass the absolute position via `sx`.
const LauncherBubble = ({ color, sx, onClick }) => (
  <Avatar onClick={onClick} sx={{ bgcolor: color, color: "#fff", width: 56, height: 56, cursor: onClick ? "pointer" : "default", ...sx }}>
    <Box sx={{ width: 28, height: 28, display: "flex" }}>
      <Message2 size="100%" color="currentColor" variant="Bold" />
    </Box>
  </Avatar>
);

/**
 * Live preview panel shared by steps 1/2 — a simplified, not pixel-perfect,
 * mock driven by the in-progress form state. "Chat window" reuses the same
 * bubble radius/typography conventions as the inbox's own ChatCard.jsx.
 * Outer zone + inset bordered card + header strip mirrors AgentConfigView's
 * own "Test Agent" panel partitioning exactly (that gutter-plus-card split,
 * not just a plain bordered box) — see the parent's wrapping Box for the
 * "zone" half of this, this component is the "card" half.
 */
const LivePreviewPanel = ({ form, previewTab, setPreviewTab }) => {
  const primary = normalizeHex(form.primaryColor, "#0066FF");
  const secondary = normalizeHex(form.secondaryColor, "#0D122C");

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      {/* Header strip — same 24x24 bordered-square icon + 1rem/500 title
          recipe as "Test Agent"'s own heading. */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            flexShrink: 0,
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.primary",
          }}
        >
          <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Monitor size="100%" color="currentColor" variant="Linear" />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "1rem", fontWeight: 500, color: "text.primary" }}>Live Preview</Typography>
      </Box>

      <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)} variant="fullWidth" sx={{ flexShrink: 0 }}>
        <Tab label="Entry point" value="entry" sx={{ fontSize: "0.8125rem", fontWeight: 600 }} />
        <Tab label="Homepage" value="homepage" sx={{ fontSize: "0.8125rem", fontWeight: 600 }} />
        <Tab label="Chat window" value="chatwindow" sx={{ fontSize: "0.8125rem", fontWeight: 600 }} />
      </Tabs>

      {/* Filled gradient dropped for a border instead (Rahim, 2026-09-23:
          "remove the inner colour and only keep the border colour"). */}
      {/* No border at all now (Rahim, 2026-09-23: "your doubling the borders
          like the left right and below remove them as well") — this box sits
          flush against the card's own outer border on every side (no gutter
          between them since the zone padding was removed), so its own border
          was just doubling up with the card's on all four edges, not just
          the top. The card's outer border alone frames it now. */}
      <Box sx={{ flex: 1, minHeight: 380, position: "relative", boxSizing: "border-box", overflow: "hidden" }}>
        {/* Entry point: the launcher bubble + popup scene — literally what
            "the launcher bubble appears in the corner" looks like (Rahim,
            2026-09-23: "duplicate the functionality to the entry point
            properly"). Homepage used to share this block; it has its own
            widget home-screen mock below now. */}
        {/* bottom bumped 70 -> 100 (Rahim, 2026-09-23: "add space in the
            bottom") — more clearance below the popup+bubble composition,
            instead of it hugging the box's bottom edge. Width 220 -> 280 and
            font swapped to our own BODY_SX token instead of an off-scale
            inline 0.75rem (Rahim: "use our font sizing... increase the size
            of the hello bubble"). */}
        {previewTab === "entry" && (
          <Box sx={{ position: "absolute", bottom: 100, right: 20, width: 280, maxWidth: "calc(100% - 40px)" }}>
            {/* position:relative + right:24 shifts the message bubble left
                while the launcher circle below stays in the corner (Rahim,
                2026-09-23: "make the hello and welcome message to the left a
                bit") — keeps the bubble's width, unlike a right margin. */}
            {/* Shadow back, but a soft low-opacity one instead of MUI's
                layered elevation shadows (Rahim, 2026-09-23: "get it back
                with light colour" — after 4 -> 1 -> none). */}
            <Box
              sx={{
                position: "relative",
                right: 24,
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                p: 2,
              }}
            >
              <Typography sx={{ ...BODY_SX, fontWeight: 500, overflowWrap: "anywhere", color: "#0D122C" }}>
                {form.popupMessage || form.welcomeMessage || "Get instant answers — start chatting with us now."}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => setPreviewTab("homepage")}
                sx={{ mt: 1.5, textTransform: "none", bgcolor: primary, "&:hover": { bgcolor: primary } }}
              >
                Start chat
              </Button>
            </Box>
            {/* Launcher bubble: emoji swapped for an Iconsax chat icon (app-wide
                icon standard) and the circle enlarged 48 -> 64 (Rahim,
                2026-09-23: "increase the size of the icon and use better
                icon"). bottom -60 -> -80 so the bigger circle keeps a gap
                below the popup instead of overlapping its bottom edge. */}
            <LauncherBubble color={primary} onClick={() => setPreviewTab("homepage")} sx={{ position: "absolute", bottom: -72, right: 0 }} />
          </Box>
        )}

        {/* Homepage tab — the widget's home screen (Rahim, 2026-09-23: "we are
            moving for the home page... show this", "just make the
            frontend"). Presentation-only mock: the heading is static (no form
            field for it), the card text follows the Pop-up message field, the
            logo follows the uploaded logo. Always-light colors on purpose —
            this is the customer-facing widget, not our app chrome, so it
            shouldn't flip with the app's dark mode. Footer says Clusterden,
            not the reference screenshot's competitor name. */}
        {(previewTab === "homepage" || previewTab === "chatwindow") && (
          <Box sx={{ position: "absolute", inset: 0 }}>
            {previewTab === "homepage" ? (
            <Box sx={{ ...widgetWindowSx(primary, secondary), p: 3, overflowY: "auto" }}>
              {/* Logo top-left, minimize top-right (Rahim, 2026-09-23). The "-"
                  goes back to the Entry point view (launcher bubble + popup),
                  i.e. "how it was for the entry point UI". */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Avatar
                  src={form.logoUrl || undefined}
                  imgProps={{ style: { objectFit: "contain", padding: 6, boxSizing: "border-box" } }}
                  sx={{ width: 48, height: 48, bgcolor: "#FFFFFF", color: primary, fontWeight: 700, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                >
                  {/* No logo uploaded -> a chat icon in the primary colour
                      (Rahim, 2026-09-23: "if there is no logo show an icon"). */}
                  <Box sx={{ width: 26, height: 26, display: "flex" }}>
                    <Message2 size="100%" color="currentColor" variant="Bold" />
                  </Box>
                </Avatar>
                <IconButton
                  size="small"
                  aria-label="Minimize"
                  onClick={() => setPreviewTab("entry")}
                  sx={{ color: "#6B7280", mt: -0.5, mr: -0.5, "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
                >
                  <Box sx={{ width: 18, height: 18, display: "flex" }}>
                    <Minus size="100%" color="currentColor" variant="Linear" />
                  </Box>
                </IconButton>
              </Box>
              <Typography sx={{ mt: 2.5, fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3, color: "#0D122C" }}>
                Always happy to help, just reach out!
              </Typography>
              <Box sx={{ mt: 3, bgcolor: "#FFFFFF", borderRadius: "12px", p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Typography sx={{ ...BODY_SX, fontWeight: 500, overflowWrap: "anywhere", color: "#0D122C" }}>
                  {form.popupMessage || "Get instant answers — start chatting with us now."}
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => setPreviewTab("chatwindow")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 2, textTransform: "none", borderRadius: 999, px: 2.5, py: 1, fontSize: "0.9375rem", fontWeight: 600, bgcolor: primary, "&:hover": { bgcolor: primary } }}
                >
                  Start chat
                </Button>
              </Box>
              <Typography sx={{ mt: "auto", pt: 3, textAlign: "center", fontSize: "0.75rem", color: "#6B7280" }}>
                Powered by{" "}
                <Box component="a" href="https://clusterden.com" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, color: "#0D122C", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                  Clusterden
                </Box>
              </Typography>
            </Box>
            ) : (
              // Matches the real deployed widget's chat screen exactly (App.jsx/
              // ChatHeader.jsx/MessageBubble.jsx/Composer.jsx in chatbot-widget/
              // widget-app) — this preview used to be its own, older mock (avatar
              // per message, centered header title, Send2-filled square button)
              // that had drifted from what visitors actually see (Rahim,
              // 2026-09-24: "the new changes we made in the chatbot panel for the
              // customer side is different from the wizard... do the same UI for
              // the test panel as well"). Plain white window (no gradient) to
              // match the real widget too — only Homepage still carries the tint.
              <Box sx={{ ...widgetWindowSx(primary, secondary), backgroundImage: "none" }}>
                {/* Header: left-aligned back, brand avatar + title, close on the right */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.5, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    aria-label="Back"
                    onClick={() => setPreviewTab("homepage")}
                    sx={{ color: "#6B7280", ml: -0.5, "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
                  >
                    <Box sx={{ width: 18, height: 18, display: "flex" }}>
                      <ArrowLeft2 size="100%" color="currentColor" variant="Linear" />
                    </Box>
                  </IconButton>
                  {form.logoUrl ? (
                    <Avatar
                      src={form.logoUrl}
                      imgProps={{ style: { objectFit: "contain", padding: 4, boxSizing: "border-box" } }}
                      sx={{ width: 28, height: 28, bgcolor: "#FFFFFF", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", flexShrink: 0 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: primary, color: "#fff", flexShrink: 0 }}>
                      <Box sx={{ width: 16, height: 16, display: "flex" }}>
                        <Message2 size="100%" color="currentColor" variant="Bold" />
                      </Box>
                    </Avatar>
                  )}
                  <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: "1rem", color: "#0D122C", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {form.liveChatDisplayName || "Live Chat"}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Minimize"
                    onClick={() => setPreviewTab("entry")}
                    sx={{ color: "#6B7280", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Messages — agent side: plain text in a light-grey pill, no
                    avatar (the header carries the brand); meta line sits BELOW
                    the message, not above, same as MessageBubble.jsx. */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#8B93A7", bgcolor: "#F5F6F8", borderRadius: "999px", px: 1.5, py: 0.5 }}>
                      Today
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                    <Box sx={{ maxWidth: "82%", width: "fit-content", px: 2, py: 1.1, borderRadius: "18px", bgcolor: "#F5F6F8" }}>
                      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 400, lineHeight: 1.55, overflowWrap: "anywhere", color: "#0D122C" }}>
                        {form.welcomeMessage || "Hello and welcome! How can I help?"}
                      </Typography>
                    </Box>
                  </Box>

                  {form.collectCustomerData && (
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "100%",
                          boxSizing: "border-box",
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: "#FFFFFF",
                          border: "1px solid #E3E5EA",
                          boxShadow: "0 2px 8px rgba(13,18,44,0.06)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0D122C", mb: 0.5 }}>
                          Let us know how we can contact you
                        </Typography>
                        <Typography sx={{ ...WIDGET_CAPTION_SX, mb: 1.25 }}>All required fields are marked with (*).</Typography>
                        <TextField size="small" fullWidth placeholder="Name *" disabled sx={widgetFieldSx(0.5)} />
                        {form.requireEmail && <TextField size="small" fullWidth placeholder="Email *" disabled sx={widgetFieldSx(1)} />}
                        {form.requirePhone && <TextField size="small" fullWidth placeholder="Phone *" disabled sx={widgetFieldSx(1)} />}
                        {form.showConsentDisclaimer && form.consentDisclaimerText && (
                          <Typography sx={{ ...WIDGET_CAPTION_SX, display: "block", mt: 1, overflowWrap: "anywhere" }}>
                            {form.consentDisclaimerText}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* A sent (visitor) message — tinted pill in the picked primary
                      colour, right-aligned, so the preview shows both sides of
                      the conversation like the real widget does. */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-end" }}>
                    <Box sx={{ maxWidth: "82%", width: "fit-content", px: 2, py: 1.1, borderRadius: "18px", bgcolor: `${primary}1F` }}>
                      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 400, lineHeight: 1.55, color: "#0D122C" }}>Sounds good, thanks!</Typography>
                    </Box>
                    <Typography sx={{ color: "#8B93A7", fontSize: "0.75rem" }}>2:15 PM Sent</Typography>
                  </Box>
                </Box>

                {/* Composer (visual only) — large rounded box, attach bottom-left,
                    mic + round send bottom-right, same as Composer.jsx. */}
                <Box sx={{ mx: 2, mt: 0.5, mb: 1, px: 2, pt: 1.25, pb: 1, borderRadius: "20px", bgcolor: "#FFFFFF", border: "1px solid #D9DCE4", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: "0.9375rem", color: "#9CA3AF" }}>Enter your message here</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ width: 20, height: 20, display: "flex", color: "#6B7280", ml: -0.5 }}>
                      <Paperclip2 size="100%" color="currentColor" variant="Linear" />
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ width: 20, height: 20, display: "flex", color: "#6B7280" }}>
                      <Microphone size="100%" color="currentColor" variant="Linear" />
                    </Box>
                    <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: primary, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Box sx={{ width: 17, height: 17, display: "flex" }}>
                        <ArrowUp size="100%" color="currentColor" variant="Linear" />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Typography sx={{ textAlign: "center", pb: 1.25, fontSize: "0.75rem", color: "#8B93A7", flexShrink: 0 }}>
                  Powered by{" "}
                  <Box component="a" href="https://clusterden.com" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, color: "inherit", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    Clusterden
                  </Box>
                </Typography>
              </Box>
            )}
            {/* Same spot as on Entry point: 20px from the right, 28px from the
                bottom (that tab's popup wrapper is bottom:100/right:20 with the
                bubble at bottom:-72). The card above ends 12px over it. */}
            <LauncherBubble color={primary} sx={{ position: "absolute", bottom: 28, right: 20 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * "Connect Website Chat" wizard — its own page (not embedded in Settings'
 * sidebar; see the AI Agents comparison, Rahim 2026-09-23), styled to match
 * AI Agent's own Create/Edit Agent view: a PanelHeading (back arrow + title
 * + Cancel) instead of the global TopNav, a violet icon-circle stepper, the
 * same LabeledField input styling, and a "Test Agent"-style partitioned
 * panel on the right. All of it is presentation only — every handler,
 * validation rule, and API call below is unchanged from before this pass.
 */
export default function ConnectChatbotWidgetPage() {
  const { accountid, workspaceId: workspaceIdParam } = useParams();
  const navigate = useNavigate();
  const currentWorkspace = useSelector((s) => s.workspace?.currentWorkspace);
  const workspaceId = workspaceIdParam || currentWorkspace?._id;

  const [activeStep, setActiveStep] = useState(0);
  // "entry" so the panel opens on the leftmost tab (Entry point), matching
  // what's actually selected/highlighted on first load — was "homepage",
  // which opened on the 2nd tab while the 1st sat unselected (Rahim,
  // 2026-09-23).
  const [previewTab, setPreviewTab] = useState("entry");
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  // Errors go through the app's real toast system (App.js's global <Toaster>,
  // top-right under the nav) — same as AI Agents' Save/validation errors —
  // instead of an inline Alert inside the form (Rahim, 2026-09-23: "position it
  // on the top right section under the top nav, all the errors for the create
  // website chat widget"). Existing call sites are unchanged: a message shows a
  // toast, "" clears it. A fixed id makes a new error replace the previous one
  // instead of stacking.
  const setErrorMessage = (message) => {
    if (message) toast.error(message, { id: "chatbot-widget-error" });
    else toast.dismiss("chatbot-widget-error");
  };
  const [result, setResult] = useState(null); // { widget, widgetSecret }

  // A workspace is only ever allowed one widget (ChatbotWidgetModel.workspaceId
  // is unique). Existing widgets use this same page in edit mode.
  const [existingCheckDone, setExistingCheckDone] = useState(false);
  const [existingWidget, setExistingWidget] = useState(null);

  const [form, setForm] = useState({
    name: "",
    allowedDomain: "",
    logoUrl: "",
    primaryColor: "#0066FF",
    secondaryColor: "#0D122C",
    liveChatDisplayName: "Live Chat",
    senderDisplayName: "Agent",
    welcomeMessage: "Hello and welcome! Got questions or need assistance? Just let me know—I'm all ears!",
    popupMessage: "",
    popupDelaySeconds: 0,
    collectCustomerData: false,
    requirePhone: true,
    requireEmail: true,
    showConsentDisclaimer: false,
    consentDisclaimerText: "",
    requireVerifiedIdentity: false,
  });

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!workspaceId) {
      setExistingCheckDone(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let token = getAccessToken();
        if (!token) token = await getAuthToken();
        // includeDeleted: a disconnected widget is soft-deleted, not removed —
        // still need its config back to prefill the wizard, and reconnecting
        // revives that same record server-side rather than inserting a new one.
        const widget = token ? await fetchChatbotWidget(workspaceId, token, { includeDeleted: true }) : null;
        if (cancelled) return;
        if (widget && !widget.deletedAt) {
          setExistingWidget(widget);
          setForm((prev) => ({
            ...prev,
            name: widget.name || prev.name,
            allowedDomain: widget.allowedDomain || prev.allowedDomain,
            logoUrl: widget.appearance?.logoUrl || "",
            primaryColor: widget.appearance?.primaryColor || prev.primaryColor,
            secondaryColor: widget.appearance?.secondaryColor || prev.secondaryColor,
            liveChatDisplayName: widget.appearance?.liveChatDisplayName || prev.liveChatDisplayName,
            senderDisplayName: widget.appearance?.senderDisplayName || prev.senderDisplayName,
            welcomeMessage: widget.welcomeMessage || prev.welcomeMessage,
            popupMessage: widget.engagement?.popupMessage || "",
            popupDelaySeconds: widget.engagement?.popupDelaySeconds || 0,
            collectCustomerData: !!widget.settings?.requireEmailBeforeChat,
            requirePhone: widget.settings?.requirePhone !== false,
            requireEmail: widget.settings?.requireEmail !== false,
            showConsentDisclaimer: !!widget.settings?.showConsentDisclaimer,
            consentDisclaimerText: widget.settings?.consentDisclaimerText || "",
            requireVerifiedIdentity: !!widget.settings?.requireVerifiedIdentity,
          }));
        } else if (widget) {
          setForm((prev) => ({
            ...prev,
            name: widget.name || prev.name,
            allowedDomain: widget.allowedDomain || prev.allowedDomain,
            logoUrl: widget.appearance?.logoUrl || "",
            primaryColor: widget.appearance?.primaryColor || prev.primaryColor,
            secondaryColor: widget.appearance?.secondaryColor || prev.secondaryColor,
            liveChatDisplayName: widget.appearance?.liveChatDisplayName || prev.liveChatDisplayName,
            senderDisplayName: widget.appearance?.senderDisplayName || prev.senderDisplayName,
            welcomeMessage: widget.welcomeMessage || prev.welcomeMessage,
            popupMessage: widget.engagement?.popupMessage || "",
            popupDelaySeconds: widget.engagement?.popupDelaySeconds || 0,
            collectCustomerData: !!widget.settings?.requireEmailBeforeChat,
            requirePhone: widget.settings?.requirePhone !== false,
            requireEmail: widget.settings?.requireEmail !== false,
            showConsentDisclaimer: !!widget.settings?.showConsentDisclaimer,
            consentDisclaimerText: widget.settings?.consentDisclaimerText || "",
            requireVerifiedIdentity: !!widget.settings?.requireVerifiedIdentity,
          }));
        }
      } catch (err) {
        if (!cancelled) setExistingWidget(null);
      } finally {
        if (!cancelled) setExistingCheckDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const goToIntegrations = () => navigate(`/home/${accountid}/settings`);

  const handleLogoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    setErrorMessage("");
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${SERVER_DOMAIN}/api/media/upload/images`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = response.data?.data?.url || response.data?.url;
      if (url) setField("logoUrl")(url);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || "Failed to upload logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const validateStep1 = () => {
    if (!form.name.trim()) {
      setErrorMessage("Widget name is required.");
      return false;
    }
    if (!ORIGIN_PATTERN.test(form.allowedDomain.trim())) {
      setErrorMessage("Enter a valid origin, e.g. https://example.com (no path, no trailing slash).");
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    setErrorMessage("");
    if (!workspaceId) {
      setErrorMessage("No workspace selected.");
      return;
    }
    setLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setErrorMessage("Not authenticated.");
        return;
      }

      const payload = {
        name: form.name.trim(),
        allowedDomain: form.allowedDomain.trim(),
        welcomeMessage: form.welcomeMessage.trim() || undefined,
        appearance: {
          primaryColor: form.primaryColor,
          secondaryColor: form.secondaryColor,
          logoUrl: form.logoUrl || null,
          liveChatDisplayName: form.liveChatDisplayName.trim() || "Live Chat",
          senderDisplayName: form.senderDisplayName.trim() || "Agent",
        },
        settings: {
          requireEmailBeforeChat: form.collectCustomerData,
          requirePhone: form.requirePhone,
          requireEmail: form.requireEmail,
          showConsentDisclaimer: form.showConsentDisclaimer,
          consentDisclaimerText: form.consentDisclaimerText,
          requireVerifiedIdentity: form.requireVerifiedIdentity,
        },
        engagement: {
          popupMessage: form.popupMessage.trim(),
          popupDelaySeconds: Number(form.popupDelaySeconds) || 0,
        },
      };
      const data = existingWidget
        ? { widget: await updateChatbotWidget(existingWidget._id, workspaceId, payload, token), widgetSecret: "" }
        : await createChatbotWidget(workspaceId, payload, token);
      setResult({ widget: data.widget, widgetSecret: data.widgetSecret });
      setExistingWidget(data.widget);
      setActiveStep(2);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || "Failed to create chat widget.");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateStep1()) return;
      setErrorMessage("");
      setPreviewTab("homepage");
      setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      handleCreate();
    }
  };

  const handleBack = () => {
    setErrorMessage("");
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const installSnippet = result?.widget?.publicKey
    ? `<script src="https://cdn.clusterden.com/chatbot.js" data-chatbot="${result.widget.publicKey}" async></script>`
    : "";

  if (!existingCheckDone) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      {/* Same shared PanelHeading AI Agents uses in place of the global
          TopNav (this route is added to Home.jsx's NO_TOP_NAV_ROUTES) —
          back arrow + title + Cancel, instead of the plain icon+h5 this
          page had before and the leftover "Settings › Integrations" green
          breadcrumb that used to show above it. */}
      <PanelHeading
        title={existingWidget ? "Edit Web Chat" : "Create Web Chat"}
        variant="full"
        leading={
          <IconButton size="small" onClick={goToIntegrations} disabled={loading} sx={{ color: (theme) => theme.palette.text.primary }}>
            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ArrowLeft2 size="100%" color="currentColor" variant="Linear" />
            </Box>
          </IconButton>
        }
        action={
          <Button variant="outlined" size="small" onClick={goToIntegrations} disabled={loading} sx={PANEL_HEADER_BUTTON_SX}>
            Cancel
          </Button>
        }
      />

      {/* No maxWidth/centering here — AI Agent's own content area doesn't cap
          or center either, it just pads from the left edge (AiSettings.jsx's
          wrapper: pl:3/pr:3, full width). The old maxWidth:1100+margin:"0
          auto" left a huge empty gutter on both sides on a wide screen
          instead of starting flush left (Rahim, 2026-09-23: "left align
          just as done in the ai page"). */}
      {/* Full AI Agent structure, finally matched exactly (Rahim, 2026-09-23:
          "the whole page is of two portions why cant you understand go check
          ai edit page") — ONE outer row (not three stacked bands), split into
          exactly two full-height portions:
          - LEFT: stepper + scrollable content + Previous/Next, all three
            stacked INSIDE this one column (only the middle piece scrolls,
            via overflow:hidden on the column + overflowY:auto on just that
            middle Box) — mirrors AgentConfigView.jsx's left column exactly.
          - RIGHT: the zone+card, a plain sibling with nothing below it, full
            height on its own. This is why Next kept sitting "under" the
            preview before — it was a page-wide footer under BOTH columns,
            when AI Agent's Next lives ONLY inside the left column. Also now
            "always-on across every step" (including Deploy), matching
            AgentConfigView's own explicit choice for Test Agent. */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3, px: 3, py: 2, boxSizing: "border-box" }}>
        <Box sx={{ flex: 1, minWidth: 0, height: { lg: "100%" }, display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}>
          {/* Stepper — same violet icon-circle treatment as AI Agent's, just
              our own 3 steps/icons instead of its 4. Display only: clicking a
              step here does nothing — Previous/Next below are still the only
              way to move between steps. */}
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              flexShrink: 0,
              "& .MuiStepConnector-alternativeLabel": { top: 20 },
              "& .MuiStepConnector-line": { borderColor: "divider" },
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: STEP_FILLED_COLOR,
              },
              "& .MuiStepLabel-label": { fontSize: "0.875rem", fontWeight: 600 },
              "& .MuiStepLabel-iconContainer": { paddingRight: 0 },
              "& .MuiStepLabel-labelContainer .MuiStepLabel-label": { marginTop: "4px" },
            }}
          >
            {STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel icon={<WizardStepIcon active={index === activeStep} completed={index < activeStep} index={index} />}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Content — only this piece scrolls, independent of the right
              portion (which never needs to, per AI Agent's own structure). */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pt: 2,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                borderRadius: "4px",
              },
            }}
          >
            {activeStep === 0 && (
              // maxWidth: PAGE_FIELD_WIDTH restored (Rahim, 2026-09-23:
              // "make the width smaller... you have literally made them for
              // full width") — the earlier dead-middle-gap problem was the
              // LEFT COLUMN's own width being wrong (fixed-width right
              // sibling instead of a real flex portion, footer spanning both
              // columns), not this cap. Now that the column itself is
              // correctly sized, capping the fields at AI Agent's own
              // SHORT_FIELD_WIDTH (480px) is safe again — single-line inputs
              // shouldn't stretch a foot wide just because their column can.
              <Stack spacing={FIELD_SPACING} sx={{ maxWidth: PAGE_FIELD_WIDTH }}>
                    <LabeledField label="Widget name">
                      <TextField
                        fullWidth
                        placeholder="e.g. Main website"
                        value={form.name}
                        onChange={(e) => setField("name")(e.target.value)}
                        disabled={loading}
                        sx={inboxInputSx}
                      />
                    </LabeledField>
                    <LabeledField label="Website URL">
                      <TextField
                        fullWidth
                        placeholder="https://example.com"
                        value={form.allowedDomain}
                        onChange={(e) => setField("allowedDomain")(e.target.value)}
                        disabled={loading}
                        helperText="The exact origin the widget will run on — scheme + domain, no path."
                        sx={inputWithHelperSx}
                      />
                    </LabeledField>

                    <SectionCard
                      heading="Brand identity"
                      description="Customize how your widget looks and appears to visitors by adding your brand elements"
                      icon={Brush}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {form.logoUrl && <Avatar src={form.logoUrl} sx={{ width: 48, height: 48 }} />}
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={logoUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                          disabled={logoUploading}
                          sx={neutralButtonSx}
                        >
                          {form.logoUrl ? "Change logo" : "Upload logo"}
                          <input type="file" accept="image/png,image/jpeg" hidden onChange={handleLogoUpload} />
                        </Button>
                        {form.logoUrl && (
                          <Button size="small" onClick={() => setField("logoUrl")("")} sx={{ textTransform: "none" }}>
                            Remove
                          </Button>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <ColorField label="Primary color" value={form.primaryColor} onChange={setField("primaryColor")} disabled={loading} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <ColorField label="Secondary color" value={form.secondaryColor} onChange={setField("secondaryColor")} disabled={loading} />
                        </Box>
                      </Stack>

                      <LabeledField label="Live chat display name">
                        <TextField fullWidth value={form.liveChatDisplayName} onChange={(e) => setField("liveChatDisplayName")(e.target.value)} disabled={loading} sx={inboxInputSx} />
                      </LabeledField>
                      <LabeledField label="Sender display name">
                        <TextField fullWidth value={form.senderDisplayName} onChange={(e) => setField("senderDisplayName")(e.target.value)} disabled={loading} sx={inboxInputSx} />
                      </LabeledField>
                      <LabeledField label="Welcome message">
                        <TextField fullWidth multiline minRows={2} value={form.welcomeMessage} onChange={(e) => setField("welcomeMessage")(e.target.value)} disabled={loading} sx={inboxInputSx} />
                      </LabeledField>
                    </SectionCard>
                  </Stack>
                )}

            {activeStep === 1 && (
              // Same SHORT_FIELD_WIDTH cap as step 0 above.
              <Stack spacing={FIELD_SPACING} sx={{ maxWidth: PAGE_FIELD_WIDTH }}>
                <SectionCard
                  heading="Pop-up message"
                  description="Greet visitors with a short message to start conversations and encourage engagement"
                  icon={Notification}
                >
                  <LabeledField label="Message content">
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={form.popupMessage}
                      onChange={(e) => setField("popupMessage")(e.target.value.slice(0, POPUP_MAX_LEN))}
                      helperText={`${form.popupMessage.length} / ${POPUP_MAX_LEN}`}
                      disabled={loading}
                      sx={inputWithHelperSx}
                    />
                  </LabeledField>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={BODY_SX}>Show this message</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={form.popupDelaySeconds}
                      onChange={(e) => setField("popupDelaySeconds")(Math.max(0, Number(e.target.value) || 0))}
                      sx={[inboxInputSx, { width: 90 }]}
                      disabled={loading}
                    />
                    <Typography sx={BODY_SX}>seconds after a visitor arrives on the page</Typography>
                  </Stack>
                </SectionCard>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.collectCustomerData}
                      onChange={(e) => {
                        setField("collectCustomerData")(e.target.checked);
                        // Turning it on jumps the preview to the Chat window, where the
                        // contact form it adds is shown (Rahim, 2026-09-23).
                        if (e.target.checked) setPreviewTab("chatwindow");
                      }}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={SECTION_TITLE_SX}>Collect customer data</Typography>
                      <Typography sx={SECTION_BODY_SX}>
                        When enabled, visitors will need to enter their contact information before starting a conversation
                      </Typography>
                    </Box>
                  }
                />
                {/* Plain-string labels don't go through Typography, so the Body
                    token is set via .MuiFormControlLabel-label directly (theme
                    default here is body1, 0.75rem). */}
                <FormControlLabel
                  control={<Checkbox checked={form.requirePhone} onChange={(e) => setField("requirePhone")(e.target.checked)} disabled={loading || !form.collectCustomerData} />}
                  label="Phone number is required"
                  sx={{ "& .MuiFormControlLabel-label": BODY_SX }}
                />
                <FormControlLabel
                  control={<Checkbox checked={form.requireEmail} onChange={(e) => setField("requireEmail")(e.target.checked)} disabled={loading || !form.collectCustomerData} />}
                  label="Email address is required"
                  sx={{ "& .MuiFormControlLabel-label": BODY_SX }}
                />

                <FormControlLabel
                  control={<Checkbox checked={form.showConsentDisclaimer} onChange={(e) => setField("showConsentDisclaimer")(e.target.checked)} disabled={loading || !form.collectCustomerData} />}
                  label="Show consent disclaimer"
                  sx={{ "& .MuiFormControlLabel-label": BODY_SX }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="By submitting, you authorize us to text/call the phone number provided and/or send emails to the email address provided…"
                  value={form.consentDisclaimerText}
                  onChange={(e) => setField("consentDisclaimerText")(e.target.value.slice(0, CONSENT_MAX_LEN))}
                  helperText={`${form.consentDisclaimerText.length} / ${CONSENT_MAX_LEN}`}
                  disabled={loading || !form.collectCustomerData || !form.showConsentDisclaimer}
                  sx={inputWithHelperSx}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.requireVerifiedIdentity}
                      onChange={(e) => setField("requireVerifiedIdentity")(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={SECTION_TITLE_SX}>Link Chats to Logged-In Users</Typography>
                      <Typography sx={SECTION_BODY_SX}>
                        When enabled, chats are only accepted from visitors your website has signed in and verified with
                        your widget secret. Anonymous visitors will not be able to chat.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            )}

            {activeStep === 2 && (
              <Stack spacing={FIELD_SPACING} sx={{ maxWidth: PAGE_FIELD_WIDTH }}>
                <Typography sx={{ ...SECTION_BODY_SX, lineHeight: 1.5 }}>
                  Your widget is ready. Copy your widget secret now — it's shown only this once and can't be retrieved
                  again (only rotated, which invalidates this one).
                </Typography>
                {/* Same label-above-field + inboxInputSx pattern as the Brand
                    identity fields (Rahim, 2026-09-23: "use the one we are
                    already using"); keys/snippet stay monospace at Small/Label. */}
                <LabeledField label="Public key">
                  <CopyField label="Public key" hideLabel value={result?.widget?.publicKey || ""} sx={deployFieldSx} />
                </LabeledField>
                <LabeledField label="Widget secret">
                  <CopyField label="Widget secret" hideLabel value={result?.widgetSecret || ""} sx={deployFieldSx} />
                </LabeledField>
                <LabeledField label="Add this to your website">
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={installSnippet}
                    InputProps={{ readOnly: true }}
                    sx={deployFieldSx}
                  />
                </LabeledField>
              </Stack>
            )}

          </Box>

          {/* Previous/Next — INSIDE the left column now (Rahim, 2026-09-23:
              "next button is still under the live preview") — AI Agent's own
              footer lives inside its left column too, so it only ever sits
              under the form, never under the right portion. No border/fill
              (Rahim, earlier: "i dont want that footer at all only need the
              buttons"). */}
          <Box sx={{ pt: 2, flexShrink: 0, display: "flex", gap: 2 }}>
            {activeStep < 2 ? (
              <>
                <Button variant="outlined" sx={footerNavButtonSx} disabled={activeStep === 0 || loading} onClick={handleBack} startIcon={<ArrowBackIcon />}>
                  Previous
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" sx={footerNavButtonSx} onClick={handleNext} disabled={loading} endIcon={loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <ArrowForwardIcon />}>
                  {activeStep === 1 ? (loading ? (existingWidget ? "Saving" : "Creating") : existingWidget ? "Save Changes" : "Create Widget") : "Next"}
                </Button>
              </>
            ) : (
              <>
                {/* Previous on the Deploy step too (Rahim, 2026-09-23: "after
                    arriving on the deploy page there is no going back option").
                    Same handler as the other steps. */}
                <Button variant="outlined" sx={footerNavButtonSx} onClick={handleBack} startIcon={<ArrowBackIcon />}>
                  Previous
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" sx={neutralButtonSx} onClick={goToIntegrations}>
                  Done
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* RIGHT portion — always rendered regardless of activeStep, matching
            AI Agent's own "always-on across every step" choice for Test
            Agent. The gutter padding around the card is gone now (Rahim,
            2026-09-23: "increase the size of the live preview card to match
            the background coloured box") — card fills the zone edge to edge. */}
        <Box
          sx={{
            width: { xs: "100%", lg: 520 },
            height: { xs: "auto", lg: "100%" },
            flexShrink: 0,
            boxSizing: "border-box",
            borderRadius: "8px",
            bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "#E9EDF2"),
          }}
        >
          <LivePreviewPanel form={form} previewTab={previewTab} setPreviewTab={setPreviewTab} />
        </Box>
      </Box>
    </Box>
  );
}
